import { THREE } from "@scrolly/renderer";
import { logError } from "../utils/logger";
import VideoMaterial from "./VideoMaterial";
import { WebGLAssets } from "./webglAssets";
import { colorToThree } from "../utils/color/threeColor";
import type { VideoContent } from "@scrolly/post-types";

const PLAY_DEBOUNCE = 100; // ms

// Legacy code typing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(THREE.VideoTexture.prototype as any).update = () => null; // we manually update

class Video {
    videoPlayer: HTMLVideoElement;
    videoMaterial: VideoMaterial | undefined;
    videoTexture: THREE.VideoTexture | undefined;
    isPlaying = false;
    hasEnded = false;
    cancelPlay: ReturnType<typeof setTimeout> | undefined;
    /**
     * Ein Start ist eingeplant, der Debounce-Timer laeuft noch. Schuetzt vor
     * Aufrufern im Frame-Takt (Crawl: "Element pausiert -> play()"): jeder
     * play()-Aufruf zog den 100ms-Timer NEU auf, bei ~16ms Frame-Abstand
     * feuerte er also NIE — das Video nach einem Live-Upload startete nur,
     * wenn der Browser zufaellig >100ms hakte (Jans WK-Fund 02.09.2026,
     * "auf Loop spielt nicht, Klick auf 1x laeuft sofort").
     */
    private playPending = false;

    // content.muted aus dem Layer-JSON. Wird in generateTexture() gesetzt.
    // play() entstummt nur, wenn dieses Flag nicht gesetzt ist — sonst
    // werden Hintergrund-Videos mit content.muted=true bei jedem play()-
    // Call entstummt, und User hören mehrere überlappende Lifetime-
    // Videos gleichzeitig (Bene-Befund 2026-05-27 auf /scrollytelling/zuviel:
    // Wetterkarte-Background-Loop hatte content.muted=true im JSON,
    // wurde aber durch play() entstummt während Boris-Becker-Video lief).
    private contentMuted: boolean = false;

    // Ping-pong state: 1 = forward, -1 = reverse
    pingPongDirection: 1 | -1 = 1;
    private lastPingPongTime: number = 0;

    // Ton-Optionen aus dem Menue (Jans 1.0: Lautstaerke + Ein-/Ausblenden).
    // Die Regler existierten im Port-Menue, der geteilte Renderer wertete sie
    // aber nirgends aus — sie liefen komplett ins Leere.
    targetVolume = 1;
    fadeInMs = 0;
    fadeOutMs = 0;
    fadeRaf: number | null = null;
    /**
     * Laeuft gerade eine AUSBLENDUNG (pause mit fadeOut)? Schuetzt davor, sie
     * pro Frame neu zu starten bzw. waehrenddessen zu springen — s. pause()
     * und seek() (Jans "Audio-Ghosts" 03.08.2026).
     */
    stoppingFade = false;

    /**
     * Adresse, die generateTexture (preload="none") NICHT an das Element
     * gegeben hat — der Reader haelt Videos ausser dem Startvideo bis zum
     * Boot-Ende (Startvideo abspielbereit) ohne Quelle, damit sie dem
     * Startvideo nicht die Leitung wegnehmen. quelleFreigeben() gibt sie
     * frei; der Vorlader setzt stattdessen ggf. direkt einen Blob.
     */
    aufgeschobeneQuelle: string | null = null;

    /** Zeitpunkt (performance.now) der Freigabe — fuers ?startdebug-HUD. */
    quelleFreigegebenBei = 0;

    /** Zurueckgehaltene Quelle an das Element geben und laden (nach dem Boot). */
    quelleFreigeben() {
        if (!this.aufgeschobeneQuelle) return;
        const quelle = this.aufgeschobeneQuelle;
        this.aufgeschobeneQuelle = null;
        this.quelleFreigegebenBei = typeof performance !== "undefined" ? performance.now() : Date.now();
        // Schon anderweitig versorgt (Vorlader-Blob)? Dann nichts tun.
        if (this.videoPlayer.src) return;
        this.videoPlayer.preload = "auto";
        this.videoPlayer.src = quelle;
        try {
            this.videoPlayer.load();
        } catch {
            /* ignore */
        }
    }

    // Zerstoerungsfreies Trim-Fenster (Sekunden); trimEnd undefined = echtes Ende
    trimStart = 0;
    trimEnd: number | undefined = undefined;
    /** Zuletzt angestossenes Seek-Ziel (Sekunden) — gegen Seek-Stuerme auf iOS. */
    seekZiel = -1;
    /**
     * Element steht ABSICHTLICH still, gilt aber logisch als spielend
     * (playVirtual/parkAtEnd: Standbild am Trim-Ende, Ping-Pong per Hand).
     * Der Renderer darf ein so geparktes Video nicht "heilen" — nur ein von
     * AUSSEN pausiertes (iOS-Unterbrechung, s. RendererWebGL Loop-Zweig).
     */
    virtuellGeparkt = false;

    constructor() {
        this.generateTexture = this.generateTexture.bind(this);
        this.seek = this.seek.bind(this);

        const video = document.createElement("video");
        video.style.display = "none";
        video.addEventListener("play", () => {
            // Zombie-Start (Safari, s. play()): beginnt die Wiedergabe, obwohl
            // zuletzt pause() galt (WebKit fuehrt einen VOR dem Puffern
            // gemerkten Play-Wunsch spaeter trotzdem aus), sofort wieder
            // stoppen — statt einen Frame lang Ton durchzulassen.
            if (!this.isPlaying) {
                video.pause();
                return;
            }
            this.isPlaying = true;
            this.hasEnded = false;
        });
        video.addEventListener("ended", () => {
            this.hasEnded = true;
            // SCHLEIFE VON HAND (Jans Fund 03.09.2026, Startvideo „hoert auf
            // abzuspielen"): Mit loop=true duerfte 'ended' gar nicht feuern —
            // WebKit tut es bei Blob-Quellen trotzdem (das Startvideo liegt
            // seit #601 als Blob vor) und bleibt am letzten Bild stehen; der
            // Renderer sah weiter isPlaying=true und startete nie neu. Soll
            // das Video in Schleife laufen, hier selbst an den Trim-Anfang
            // und weiter — in Browsern, die korrekt loopen, feuert das nie.
            if (video.loop && this.isPlaying) {
                this.hasEnded = false;
                try {
                    video.currentTime = this.trimStart;
                } catch {
                    /* ignore */
                }
                const pr = video.play();
                if (pr && typeof pr.catch === "function") pr.catch(() => {});
            }
        });
        this.videoPlayer = video;
        // An der zentralen Medien-Steuerung anmelden (Jans 1.0: registerScrollyAudio),
        // damit ein globales Stopp — z. B. Klick auf "Dashboard" — auch den
        // Video-Ton pausiert. Reader: Hook null, unveraendert.
        WebGLAssets.registerMediaElement?.(video);
    }

    generateNormalVideo(props: VideoContent) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const videoTexture:any = new THREE.VideoTexture(this.videoPlayer);
        this.videoTexture = videoTexture;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.format = THREE.RGBAFormat;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getThreeColorSpace: any = THREE;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const texture_:any = (videoTexture as THREE.VideoTexture as THREE.Texture)/*["colorSpace"] = getThreeColorSpace.SRGBColorSpace;*/
        texture_.colorSpace = getThreeColorSpace.SRGBColorSpace;

        const material = new THREE.MeshBasicMaterial({
            map: videoTexture,
            alphaTest: 0.01,
            transparent: true,
            depthWrite: false, // removes artifacts
            side: props.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
        });

        return {
            material,
            texture: videoTexture,
            height: this.videoPlayer.videoHeight,
            width: this.videoPlayer.videoWidth,
        };
    }

    generateTransparentVideo(props: VideoContent) {
        this.videoMaterial = new VideoMaterial(this.videoPlayer, {
            // Unsere Color ist 0..255 (Farbwaehler), THREE.Color 0..1 — ohne die
            // Umrechnung landeten Werte bis 255 im Shader und die Maske verglich
            // gegen eine voellig andere Farbe (Jans 1.0 rechnet hier um).
            chromaKey: colorToThree(props.chromaKey),
            transparent: props.transparent,
            tolerance: props.tolerance,
        });

        return {
            texture: this.videoMaterial.texture,
            material: this.videoMaterial.material,
            height: this.videoPlayer.videoHeight,
            width: this.videoPlayer.videoWidth,
        };
    }

    generateTexture(
        props: VideoContent,
        url: string,
        /**
         * Vorlade-Verhalten des Elements. "none" fuer Videos, die beim Boot
         * NICHT mitladen sollen (alle ausser dem Startvideo im Reader, Jans
         * Slow-4G-Test 05.09.2026) — der Renderer schaltet nach START auf
         * "auto" und ruft load() (s. RendererWebGL, nachStartNachgeladen).
         */
        preload: "auto" | "none" | "metadata" = "auto"
    ) {
        return new Promise<{
            material: THREE.ShaderMaterial | THREE.MeshBasicMaterial;
            texture: THREE.Texture;
            height: number;
            width: number;
        }>((resolve) => {
            // Trim gehoert dem Wrapper ab dem ERSTEN Laden (Jans iPhone-HUD
            // 02.09.2026: trim=0.0 am Wrapper, waehrend der Inhalt 5,7 s trug —
            // setTrim kam erst spaeter, und bis dahin rechnete alles mit 0).
            if ((props.trimStart ?? 0) > 0 && this.trimStart === 0) this.trimStart = props.trimStart!;
            if (props.trimEnd !== undefined && this.trimEnd === undefined) this.trimEnd = props.trimEnd;
            this.videoPlayer.addEventListener("loadedmetadata", () => {
                // TRIM-VORSPULEN (Jans iPhone-Fund 02.09.2026, „Video springt am
                // Anfang"): Ein getrimmtes Video stand bis zum ersten play() auf
                // Bild 0 — das erste gezeigte Bild war also eine Stelle, die nie
                // zu sehen sein soll, und beim Abspielen sprang es sichtbar zum
                // Trim-Anfang (auf iOS mit Nachlade-Pause dazwischen). Sofort
                // nach den Metadaten an den Trim-Anfang stellen: Das erste
                // dekodierte Bild ist dann schon das richtige.
                //
                // BEI JEDEM LADEN, nicht nur beim ersten (Jans iPhone-HUD,
                // „DANACH"): Der Vorlader tauscht die Quelle auf einen Blob —
                // ein neuer Ladevorgang setzt die Position auf 0 zurueck, das
                // Vorspulen war weg, und genau beim Eintritt in die Lebenszeit
                // sprang das Video sichtbar von Bild 0 zum Trim-Anfang.
                this.vorspulenAufTrim();
                // Textur/Material nur EINMAL bauen — der Listener feuert nach
                // jedem Quellenwechsel erneut; ein zweites VideoTexture haette
                // niemand benutzt (und den Zeiger der Render-Schleife umgebogen).
                if (this.videoTexture || this.videoMaterial) return;
                const video = props.transparent
                    ? this.generateTransparentVideo(props)
                    : this.generateNormalVideo(props);
                resolve(video);
            });
            this.videoPlayer.crossOrigin = "anonymous";
            this.videoPlayer.preload = preload;
            // Editor-Uploads liegen als "local-video:<id>" in der IndexedDB —
            // roh als src gesetzt kann der Browser damit nichts anfangen und das
            // Video blieb schwarz (gleiche Falle wie zuvor bei HDRI und den
            // 3D-Texturen). Erst aufloesen, dann laden; Reader: Hook null, die
            // veroeffentlichte URL laeuft unveraendert durch.
            const needsResolve =
                typeof url === "string" && url.startsWith("local-") && !!WebGLAssets.resolveLocalMediaUrl;
            // QUELLE ZURUECKHALTEN (Jans Slow-4G-Test 05.09.2026): preload="none"
            // reicht nicht — Chromium holte die Datei trotzdem, sobald das
            // Element eine Quelle hatte (im Browser-Pane sofort, in Jans Chrome
            // beim Boot-Ende). Verlaesslich ist nur: gar keine src setzen. Die
            // Adresse wird gemerkt und erst mit quelleFreigeben() (nach dem
            // Boot) oder durch den Vorlader (Blob) an das Element gegeben.
            const zurueckhalten = preload === "none" && !needsResolve;
            if (zurueckhalten) {
                this.aufgeschobeneQuelle = url;
            } else if (needsResolve) {
                WebGLAssets.resolveLocalMediaUrl!(url).then((resolved) => {
                    this.videoPlayer.src = resolved || url;
                    this.videoPlayer.load();
                });
            } else {
                this.videoPlayer.src = url;
            }
            this.videoPlayer.loop = props.loop;
            this.videoPlayer.muted = props.muted;
            this.contentMuted = !!props.muted;
            if (props.muted) {
                this.videoPlayer.setAttribute("muted", "");
            }

            this.videoPlayer.playsInline = true;
            this.videoPlayer.setAttribute("playsinline", "");
            this.videoPlayer.addEventListener("error", (err) => {
                const video = props.transparent
                    ? this.generateTransparentVideo(props)
                    : this.generateNormalVideo(props);
                resolve(video);
                logError(err as unknown as Error);
            });
            // Bei asynchroner Aufloesung laedt der then()-Zweig oben — ein load()
            // ohne src wuerde hier sofort "error" feuern und das Promise mit
            // Groesse 0 aufloesen, bevor das echte Video da ist. Gleiches gilt
            // fuer die zurueckgehaltene Quelle: load() erst in quelleFreigeben().
            if (!needsResolve && !zurueckhalten) this.videoPlayer.load();

            this.videoPlayer.addEventListener("playing", (_event) => {
                console.log("Video is no longer paused");
            });
        });
    }

    // Trim-Fenster setzen (Jans 1.0, 1:1). Zerstoerungsfrei: das Video wird
    // nicht geschnitten, play()/seekToEnd() halten sich nur an die Grenzen.
    setTrim(trimStart: number, trimEnd: number | undefined) {
        this.trimStart = Math.max(trimStart, 0);
        this.trimEnd = trimEnd;
    }

    // Lautstaerke + Ein-/Ausblenddauer (Jans 1.0, 1:1). Aenderungen greifen
    // live, solange gerade kein Fade laeuft.
    /**
     * Stummschaltung LIVE umsetzen (Jans Fund 08.09.2026): `muted` zaehlte im
     * Renderer nicht zu den Live-Schluesseln — jeder Klick auf „Stumm" galt
     * als Content-Aenderung und baute das Video komplett neu (neues Element,
     * load(), Neustart) — sichtbares Zucken auf der Buehne. Jetzt nur das
     * Element umschalten; die Hoerbarkeits-Schicht (unmute-on-play) liest
     * weiter `contentMuted`.
     */
    setMuted(muted: boolean) {
        const stumm = !!muted;
        // NUR auf eine echte Aenderung des Nutzer-Schalters reagieren (Jans
        // Fund 16.09.2026, "Ton total abgehackt" bei einem ausgeblendeten
        // Video): updateVideo ruft das hier pro Bild auf. Die Hoerbarkeits-
        // Schicht (videoAudible, Deckkraft/Blickfeld) schaltet das Element
        // stumm — der alte Vergleich mit `videoPlayer.muted` sah darin eine
        // Abweichung und entstummte es pro Bild wieder; die Hoerbarkeits-
        // Schicht blendete darauf erneut aus: 400-ms-Haecksler statt Stille.
        if (this.contentMuted === stumm) return;
        this.contentMuted = stumm;
        if (stumm) {
            this.videoPlayer.muted = true;
            this.videoPlayer.setAttribute("muted", "");
        } else {
            this.videoPlayer.removeAttribute("muted");
            // Entstummen nur, wenn das Video gerade laeuft — sonst uebernimmt
            // der Play-Pfad (Auto-Play-Policy: erst nach Nutzergeste).
            if (this.isPlaying && !WebGLAssets.isMediaSuppressed?.()) {
                this.videoPlayer.muted = false;
            }
        }
    }

    setAudioOptions(volume: number, fadeInMs: number, fadeOutMs: number) {
        this.targetVolume = Math.min(Math.max(volume, 0), 1);
        this.fadeInMs = fadeInMs;
        this.fadeOutMs = fadeOutMs;
        if (this.isPlaying && this.fadeRaf === null) {
            this.videoPlayer.volume = this.targetVolume;
        }
    }

    // Lautstaerke-Rampe (Jans 1.0, 1:1).
    startFade(from: number, to: number, duration: number, onDone?: () => void) {
        if (this.fadeRaf) cancelAnimationFrame(this.fadeRaf);
        this.fadeRaf = null;
        if (duration <= 0) {
            this.videoPlayer.volume = to;
            onDone?.();
            return;
        }
        const startTime = performance.now();
        const tick = () => {
            const progress = Math.min((performance.now() - startTime) / duration, 1);
            this.videoPlayer.volume = Math.min(Math.max(from + (to - from) * progress, 0), 1);
            if (progress < 1) {
                this.fadeRaf = requestAnimationFrame(tick);
            } else {
                this.fadeRaf = null;
                onDone?.();
            }
        };
        this.fadeRaf = requestAnimationFrame(tick);
    }

    // Am Trim-Ende stehen bleiben statt weiterzulaufen (Jans 1.0): anhalten,
    // aber logisch als "spielend" markiert lassen — sonst wuerde der Renderer
    // es im naechsten Bild sofort neu starten.
    parkAtEnd() {
        this.virtuellGeparkt = true;
        clearTimeout(this.cancelPlay);
        this.playPending = false;
        if (this.fadeRaf) cancelAnimationFrame(this.fadeRaf);
        this.fadeRaf = null;
        this.stoppingFade = false;
        this.isPlaying = true;
        this.videoPlayer.pause();
    }

    playVirtual() {
        this.virtuellGeparkt = true;
        this.isPlaying = true;
    }

    pauseVirtual() {
        this.isPlaying = false;
    }

    delete() {
        WebGLAssets.unregisterMediaElement?.(this.videoPlayer);
        this.videoPlayer.pause();
        // Clear src and force release of network/decode resources
        this.videoPlayer.removeAttribute("src");
        this.videoPlayer.load();
        this.videoPlayer.remove();
        // Dispose Three.js video texture (frees GPU memory)
        if (this.videoTexture) {
            this.videoTexture.dispose();
            this.videoTexture = undefined;
        }
        // Dispose video material if using chroma-key shader. dispose() meldet
        // zusaetzlich den Bild-Callback ab — ohne das liefe er nach dem Entfernen
        // des Videos weiter und hielte Element samt Textur am Leben.
        if (this.videoMaterial) {
            this.videoMaterial.material.dispose();
            this.videoMaterial.dispose();
            this.videoMaterial = undefined;
        }
    }

    play() {
        // Stille Kachel-Aufnahme: nichts starten (Tonfetzen, s. WebGLAssets).
        if (WebGLAssets.stillCapture) return;
        this.virtuellGeparkt = false;
        // Nicht (neu) starten, solange Medien global unterdrueckt sind — etwa
        // beim Verlassen des Editors (Jans 1.0, gleiche Stelle). Sonst startet
        // ein Video nach dem Stopp durch den naechsten Tick wieder.
        if (WebGLAssets.isMediaSuppressed?.()) return;
        // Nie starten, waehrend der Tab versteckt ist (Jans Fund 02.08.2026,
        // Chrome: "Tab gewechselt -> Video-Ton spielt wieder"). Der Ablauf war
        // ein Wettlauf: visibilitychange pausiert alle Videos, aber EIN schon
        // eingeplanter RAF-Frame laeuft danach noch durch, sieht "im Fenster +
        // spielt nicht" und ruft play(); der Debounce-Timer feuert auch im
        // versteckten Tab — und weil RAF dort steht, raeumt kein Frame mehr
        // auf. Kommt der Tab zurueck, spielt der naechste Frame regulaer an.
        if (typeof document !== "undefined" && document.hidden) return;
        // Ein bereits eingeplanter Start bleibt stehen — nicht neu aufziehen
        // (s. playPending oben; sonst schiebt der Frame-Takt den Timer ewig
        // vor sich her und die Wiedergabe beginnt nie).
        if (this.isPlaying && this.playPending) return;
        this.isPlaying = true;
        clearTimeout(this.cancelPlay);
        if (this.fadeRaf) cancelAnimationFrame(this.fadeRaf);
        this.fadeRaf = null;
        // Wiedergabe gewuenscht -> eine laufende Ausblendung ist hinfaellig
        this.stoppingFade = false;
        // Innerhalb des Trim-Fensters starten: steht der Playhead davor oder ist
        // das Fenster schon durchlaufen, wieder auf trimStart setzen (Jans 1.0).
        const current = this.videoPlayer.currentTime;
        if (current < this.trimStart || (this.trimEnd !== undefined && current >= this.trimEnd)) {
            this.videoPlayer.currentTime = this.trimStart;
        }
        const _play = async () => {
            this.playPending = false;
            if (WebGLAssets.isMediaSuppressed?.()) return;
            // Zwischen Planung (Debounce) und Ausfuehrung kann laengst wieder
            // pausiert worden sein (schnelles Weiterscrollen) — dann gar nicht
            // erst starten. Gleiches gilt fuer einen inzwischen versteckten
            // Tab (s. play()): der Timer feuert dort weiter, RAF nicht.
            if (!this.isPlaying) return;
            if (typeof document !== "undefined" && document.hidden) return;
            try {
                // bei Einblendung leise starten und hochfahren, sonst direkt auf
                // die eingestellte Lautstaerke (statt fix 1.0 wie bisher)
                this.videoPlayer.volume = this.fadeInMs > 0 ? 0 : this.targetVolume;
                await this.videoPlayer.play();
                // SAFARI-ZOMBIE-PLAY (Jans Fund 02.08.2026, "Video-Ton spielt
                // beim Weiterscrollen immer wieder"): fordert man play() an,
                // BEVOR das Video gepuffert ist, merkt sich WebKit den Wunsch
                // intern und STARTET die Wiedergabe erst, wenn Daten da sind —
                // auch wenn dazwischen laengst pause() lief. Das play()-Promise
                // loest genau dann (spaet) ein. Deshalb NACH dem Await den
                // aktuellen Soll-Zustand pruefen und einen inzwischen
                // unerwuenschten Start sofort wieder stoppen.
                if (!this.isPlaying || WebGLAssets.isMediaSuppressed?.()) {
                    this.videoPlayer.pause();
                    return;
                }
                this.startFade(0, this.targetVolume, this.fadeInMs);
                // Nur entstummen wenn content.muted=false war. Die unmute-
                // on-play existiert weil Browser-Auto-Play-Policy Videos
                // initial mutet — nach User-Klick darf entstummt werden.
                // Aber content.muted=true (Background-Loop) muss respektiert
                // werden, sonst Audio-Doppel bei überlappenden Lifetimes.
                if (!this.contentMuted) {
                    this.videoPlayer.muted = false;
                }
            } catch (e) {
                logError(e as Error);
            }
        };

        this.playPending = true;
        this.cancelPlay = setTimeout(_play, PLAY_DEBOUNCE);
    }

    pause() {
        // Stille Kachel-Aufnahme: ein laufendes Video nicht anhalten — es
        // wuerde beim Zuruecksprung neu gestartet (hoerbarer Neuanfang).
        if (WebGLAssets.stillCapture && this.isPlaying) return;
        this.virtuellGeparkt = false;
        clearTimeout(this.cancelPlay);
        this.playPending = false;
        this.isPlaying = false;
        // Ausblenden, dann pausieren (Jans 1.0). Ohne eingestellte Ausblendung
        // wie bisher sofort stoppen. Im VERSTECKTEN Tab ebenfalls sofort: der
        // Fade laeuft ueber RAF, und RAF steht dort — die Ausblendung wuerde
        // nie fertig und das Video spielte hoerbar weiter.
        const hidden = typeof document !== "undefined" && document.hidden;
        if (this.fadeOutMs > 0 && !this.videoPlayer.paused && !hidden) {
            // LAEUFT SCHON EINE AUSBLENDUNG? Dann NICHT neu anstossen (Jans
            // Ghost-Fund 03.08.2026). Der Renderer ruft pause() ausserhalb der
            // Lebenszeit JEDEN Frame; jeder Aufruf startete den Fade neu, der
            // Fortschritt sprang auf 0 zurueck — bei Jans fadeOut=10000 wurde
            // die Ausblendung NIE fertig, das onDone-pause() feuerte nie und
            // das Video lief (leise, aber hoerbar) endlos weiter.
            if (this.stoppingFade) return;
            this.stoppingFade = true;
            this.startFade(this.videoPlayer.volume, 0, this.fadeOutMs, () => {
                this.stoppingFade = false;
                this.videoPlayer.pause();
            });
        } else {
            this.stoppingFade = false;
            if (this.fadeRaf) cancelAnimationFrame(this.fadeRaf);
            this.fadeRaf = null;
            this.videoPlayer.pause();
        }
    }

    seek(timer: number) {
        // Stille Kachel-Aufnahme: ein LAUFENDES Video nicht reissen; pausierte
        // (gescrubbte) duerfen springen, damit die Kachel das richtige Bild zeigt.
        if (WebGLAssets.stillCapture && this.isPlaying && !this.videoPlayer.paused) return;
        clearTimeout(this.cancelPlay);
        // Der eingeplante Start ist damit abgesagt — Flag mit zuruecksetzen,
        // sonst blockierte der playPending-Guard jeden spaeteren play().
        this.playPending = false;
        // Waehrend einer laufenden Ausblendung NICHT springen (Jans
        // Ghost-Fund): der Renderer ruft ausserhalb der Lebenszeit pro Frame
        // seek(resetTo) — das riss das noch klingende Video staendig an den
        // Anfang zurueck, man hoerte denselben Ton-Anfang in Fetzen
        // ("Bruchstuecke"). Nach dem Fade sitzt die Position ohnehin wieder.
        if (this.stoppingFade) return;
        // Nur seeken, wenn sich die Position wirklich aendert: die Ruhe-Zweige
        // rufen seek(resetTo) JEDEN Frame — in Safari stoesst jeder Seek eine
        // neue Lade-/Fertig-Runde an, und jede davon kann einen intern noch
        // gemerkten Play-Wunsch wieder ausfuehren (Zombie-Play, s. play()).
        // Nebenbei entfaellt das staendige Re-Dekodieren (Netz-/CPU-Churn).
        if (Math.abs(this.videoPlayer.currentTime - timer) < 0.05) return;
        // iOS-SEEK-STURM (Jans iPhone-HUD 02.09.2026): Auf dem Telefon dauert
        // ein Seek im Stream laenger als ein Frame — die Ruhe-Zweige riefen
        // seek(trim) in JEDEM Frame, und jeder neue Seek bricht den laufenden
        // ab: die Position kam nie an (t=0.0 bei trim 5,7). Laeuft bereits ein
        // Seek auf dasselbe Ziel, nicht erneut anstossen.
        if (this.videoPlayer.seeking && Math.abs(this.seekZiel - timer) < 0.05) return;
        this.seekZiel = timer;
        this.videoPlayer.currentTime = timer;
    }

    /** Position auf den Trim-Anfang stellen — nach den Metadaten jedes Ladens. */
    vorspulenAufTrim() {
        const ziel = this.trimStart;
        if (!(ziel > 0)) return;
        if (this.videoPlayer.currentTime >= ziel - 0.05) return;
        if (this.videoPlayer.seeking && Math.abs(this.seekZiel - ziel) < 0.05) return;
        try {
            this.seekZiel = ziel;
            this.videoPlayer.currentTime = ziel;
        } catch {
            /* Element noch nicht suchbar — play() setzt es spaeter */
        }
    }

    seekToEnd() {
        clearTimeout(this.cancelPlay);
        this.playPending = false;
        // ans Ende des Trim-Fensters, nicht ans Dateiende (Jans 1.0)
        this.videoPlayer.currentTime = this.trimEnd ?? this.videoPlayer.duration;
    }

    /**
     * Advance currentTime manually for ping-pong mode.
     * Video must be paused; we drive currentTime by deltaTime (browsers do not
     * support a negative playbackRate, so the reverse leg has to be driven by
     * hand — and then the forward leg too, so both run at the same pace).
     *
     * WENDEPUNKTE SIND DIE TRIM-MARKEN, nicht Dateianfang/-ende (Jans Fund
     * 03.08.2026: "vor & zurueck scheint nicht im renderer zu funktionieren").
     * Vorher lief das Pendel ueber die GANZE Datei. Bei einem langen Video in
     * einer kurzen Ebenen-Lebenszeit wurde das Dateiende nie erreicht — man sah
     * also nur das Vorwaerts, nie das Zurueck. Alle anderen Modi (scroll, once,
     * der Ruecksprung) rechnen laengst im Trim-Fenster; dieser hier war der
     * einzige Ausreisser.
     */
    updatePingPong() {
        const now = performance.now();
        if (this.lastPingPongTime === 0) {
            this.lastPingPongTime = now;
            return;
        }
        const delta = (now - this.lastPingPongTime) / 1000; // seconds
        this.lastPingPongTime = now;

        const duration = this.videoPlayer.duration;
        // Noch keine Metadaten (duration NaN): nichts tun, sonst wandert
        // currentTime auf NaN und das Element bleibt endgueltig stehen.
        if (!Number.isFinite(duration) || duration <= 0) return;

        const start = this.trimStart ?? 0;
        const end = this.trimEnd ?? duration;
        // Unbrauchbares Fenster (Trim-Marken verdreht oder deckungsgleich):
        // lieber die volle Datei als gar keine Bewegung.
        const [von, bis] = end - start > 0.05 ? [start, end] : [0, duration];

        // Ausserhalb des Fensters (z. B. direkt nach einem Ruecksprung) erst
        // hineinklemmen, damit der erste Schritt nicht ins Leere zeigt.
        const jetztPos = Math.min(Math.max(this.videoPlayer.currentTime, von), bis);
        let newTime = jetztPos + this.pingPongDirection * delta;

        // Bounce at boundaries
        if (newTime >= bis) {
            newTime = bis - 0.01;
            this.pingPongDirection = -1;
        } else if (newTime <= von) {
            newTime = von;
            this.pingPongDirection = 1;
        }

        this.videoPlayer.currentTime = newTime;
    }

    resetPingPong() {
        this.pingPongDirection = 1;
        this.lastPingPongTime = 0;
    }
}

export default Video;
